import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

export const useUserRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>('viewer');
  const [loading, setLoading] = useState(true);

  const fetchUserRole = async () => {
    if (!user) {
      setRole('viewer');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use the database function to get user role
      const { data, error } = await supabase
        .rpc('get_user_role', { _user_id: user.id });

      if (error) {
        console.warn('Error fetching user role, defaulting to viewer:', error);
        setRole('viewer');
        return;
      }

      setRole(data || 'viewer');
    } catch (error) {
      console.warn('Error fetching user role, defaulting to viewer:', error);
      setRole('viewer'); // Default to viewer on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserRole();
  }, [user]);

  const hasRole = (requiredRole: UserRole): boolean => {
    const roleHierarchy = { viewer: 0, contributor: 1, admin: 2 };
    return roleHierarchy[role] >= roleHierarchy[requiredRole];
  };

  const isViewer = () => role === 'viewer';
  const isContributor = () => hasRole('contributor');
  const isAdmin = () => role === 'admin';

  return {
    role,
    loading,
    hasRole,
    isViewer,
    isContributor,
    isAdmin,
    refetch: fetchUserRole,
  };
};